export type ThemeMode = 'light' | 'dark'

const BRAND_SUBTLE_ALPHA: Record<ThemeMode, number> = {
  light: 0.03,
  dark: 0.03
}

const rgbaFromHex = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '')
  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const FixedColors = {
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent'
} as const

export const ExternalColors = {
  idinBlue: '#00B0F0',
  androidPrimary: '#023C69',
  sentimentNeutral: '#F1C40F'
} as const

export const IllustrationColors = {
  cases: {
    paper: '#FBFAFA',
    panel: '#EFEBE9',
    accent: '#84726F'
  },
  cogs: {
    paper: '#FBFAFA',
    metal: '#B6A399',
    accent: '#84726F',
    panel: '#EFEBE9'
  },
  lock: {
    paper: '#FBFAFA',
    body: '#5D554F',
    accent: '#B3A49A'
  },
  success: {
    paper: '#FAFAFA',
    fill: '#87C104',
    onFill: FixedColors.white
  }
} as const

const Palette = {
  brandFallback: {
    light: '#E95E40',
    dark: '#F27358'
  },
  neutral: {
    light: {
      canvas: '#F0F3F6',
      surface: '#FFFFFF',
      chrome: '#FBFBFB',
      info: '#EDEBE9',
      soft: '#D9DFE8',
      textPrimary: '#48423E',
      textSecondary: '#4B423E',
      textMuted: '#8D8D8D',
      iconSecondary: '#84726F',
      borderSoft: '#F0F3F6',
      borderDefault: '#D9DFE8',
      borderStrong: '#4B423E',
      inverseSurface: '#48423E',
      neutralStrongSurface: '#84726F'
    },
    dark: {
      canvas: '#191817',
      surface: '#292624',
      chrome: '#191817',
      info: '#292624',
      soft: '#34312F',
      textPrimary: '#F2EFED',
      textSecondary: '#DED7D4',
      textMuted: '#AAA3A0',
      iconSecondary: '#C1B4B0',
      borderSoft: '#34312F',
      borderDefault: '#34312F',
      borderStrong: '#857B75',
      inverseSurface: '#F2EFED',
      neutralStrongSurface: '#C1B4B0'
    }
  },
  status: {
    success: {
      foreground: {
        light: '#1A4700',
        dark: '#82BD5D'
      },
      strong: {
        light: '#87C104',
        dark: '#9ACF45'
      },
      subtle: {
        light: '#EDF9E5',
        dark: 'rgba(154, 207, 69, 0.14)'
      }
    },
    warning: {
      foreground: {
        light: '#FF9C00',
        dark: '#FFB340'
      },
      accent: {
        light: '#DE8710',
        dark: '#F2A23A'
      },
      subtle: {
        light: '#FFFAE9',
        dark: 'rgba(255, 179, 64, 0.14)'
      }
    },
    danger: {
      foreground: {
        light: '#CD0404',
        dark: '#FF6868'
      },
      strong: {
        light: '#AF0000',
        dark: '#E64D4D'
      },
      subtle: {
        light: '#FFEEEE',
        dark: 'rgba(255, 104, 104, 0.14)'
      }
    },
    whatsapp: {
      light: '#00A100',
      dark: '#42C95A'
    }
  }
} as const

type ThemeColorOptions = {
  agencyBrand?: string
  applyAgencyBrandInDark?: boolean
}

export type ThemeColors = {
  brand: {
    primary: string
    onPrimary: string
  }
  background: {
    app: string
    chrome: string
  }
  surface: {
    page: string
    card: string
    hover: string
    cardDisabled: string
    modal: string
    input: string
    inputPrefix: string
    info: string
    chip: string
    chipSelected: string
    inverse: string
    brandSubtle: string
    track: string
    logoBackdrop: string
  }
  text: {
    primary: string
    secondary: string
    muted: string
    placeholder: string
    link: string
    brand: string
    inverse: string
    onBrand: string
    onDanger: string
    onNeutralStrong: string
  }
  icon: {
    primary: string
    secondary: string
    muted: string
    brand: string
    inverse: string
    onBrand: string
    onDanger: string
    onNeutralStrong: string
  }
  border: {
    transparent: string
    subtle: string
    default: string
    strong: string
    focus: string
    brand: string
    warning: string
    danger: string
    inverse: string
  }
  action: {
    primary: {
      background: string
      foreground: string
      border: string
    }
    secondary: {
      background: string
      foreground: string
      border: string
    }
    outline: {
      background: string
      foreground: string
      border: string
    }
    destructive: {
      background: string
      foreground: string
      border: string
    }
    destructiveOutline: {
      background: string
      foreground: string
      border: string
    }
    whatsappFilled: {
      background: string
      foreground: string
      border: string
    }
    whatsappOutline: {
      background: string
      foreground: string
      border: string
    }
  }
  status: {
    success: {
      foreground: string
      strong: string
      subtle: string
      onStrong: string
    }
    warning: {
      foreground: string
      strong: string
      subtle: string
      onStrong: string
    }
    danger: {
      foreground: string
      strong: string
      subtle: string
      onStrong: string
    }
  }
  sentiment: {
    positive: string
    neutral: string
    negative: string
  }
  integrations: {
    whatsapp: string
  }
  overlay: {
    scrim: string
    scrimSoft: string
    toast: string
  }
  shadow: {
    default: string
  }
  fixed: typeof FixedColors
  external: typeof ExternalColors
  illustration: typeof IllustrationColors
}

export const createThemeColors = (
  mode: ThemeMode,
  {
    agencyBrand,
    applyAgencyBrandInDark = false
  }: ThemeColorOptions = {}
): ThemeColors => {
  const neutral = Palette.neutral[mode]
  const success = {
    foreground: Palette.status.success.foreground[mode],
    strong: Palette.status.success.strong[mode],
    subtle: Palette.status.success.subtle[mode]
  }
  const warning = {
    foreground: Palette.status.warning.foreground[mode],
    accent: Palette.status.warning.accent[mode],
    subtle: Palette.status.warning.subtle[mode]
  }
  const danger = {
    foreground: Palette.status.danger.foreground[mode],
    strong: Palette.status.danger.strong[mode],
    subtle: Palette.status.danger.subtle[mode]
  }
  const shouldApplyAgencyBrand = Boolean(
    agencyBrand &&
      (mode === 'light' || applyAgencyBrandInDark)
  )
  const brandPrimary =
    shouldApplyAgencyBrand && agencyBrand
      ? agencyBrand
      : Palette.brandFallback[mode]
  const onBrandPrimary = mode === 'dark' ? FixedColors.black : FixedColors.white
  const brandSubtle = rgbaFromHex(brandPrimary, BRAND_SUBTLE_ALPHA[mode])
  const whatsapp = Palette.status.whatsapp[mode]

  return {
    brand: {
      primary: brandPrimary,
      onPrimary: onBrandPrimary
    },
    background: {
      app: neutral.canvas,
      chrome: neutral.chrome
    },
    surface: {
      page: neutral.surface,
      card: neutral.surface,
      hover: mode === 'dark' ? neutral.soft : neutral.info,
      cardDisabled: neutral.soft,
      modal: neutral.surface,
      input: neutral.surface,
      inputPrefix: neutral.info,
      info: neutral.info,
      chip: neutral.chrome,
      chipSelected: neutral.neutralStrongSurface,
      inverse: neutral.inverseSurface,
      brandSubtle,
      track: neutral.inverseSurface,
      logoBackdrop: Palette.neutral.light.chrome
    },
    text: {
      primary: neutral.textPrimary,
      secondary: neutral.textSecondary,
      muted: neutral.textMuted,
      placeholder: neutral.textMuted,
      link: brandPrimary,
      brand: brandPrimary,
      inverse: neutral.surface,
      onBrand: onBrandPrimary,
      onDanger: FixedColors.white,
      onNeutralStrong: FixedColors.white
    },
    icon: {
      primary: neutral.textPrimary,
      secondary: neutral.iconSecondary,
      muted: neutral.textMuted,
      brand: brandPrimary,
      inverse: neutral.surface,
      onBrand: onBrandPrimary,
      onDanger: FixedColors.white,
      onNeutralStrong: FixedColors.white
    },
    border: {
      transparent: FixedColors.transparent,
      subtle: neutral.borderSoft,
      default: neutral.borderDefault,
      strong: neutral.borderStrong,
      focus: neutral.iconSecondary,
      brand: brandPrimary,
      warning: warning.accent,
      danger: danger.foreground,
      inverse: neutral.surface
    },
    action: {
      primary: {
        background: brandPrimary,
        foreground: onBrandPrimary,
        border: brandPrimary
      },
      secondary: {
        background: neutral.surface,
        foreground: neutral.textPrimary,
        border: neutral.borderDefault
      },
      outline: {
        background: FixedColors.transparent,
        foreground: neutral.iconSecondary,
        border: neutral.iconSecondary
      },
      destructive: {
        background: danger.strong,
        foreground: FixedColors.white,
        border: danger.strong
      },
      destructiveOutline: {
        background: FixedColors.transparent,
        foreground: danger.strong,
        border: danger.strong
      },
      whatsappFilled: {
        background: whatsapp,
        foreground: FixedColors.white,
        border: whatsapp
      },
      whatsappOutline: {
        background: FixedColors.transparent,
        foreground: whatsapp,
        border: whatsapp
      }
    },
    status: {
      success: {
        foreground: success.foreground,
        strong: success.strong,
        subtle: success.subtle,
        onStrong: FixedColors.white
      },
      warning: {
        foreground: warning.foreground,
        strong: warning.foreground,
        subtle: warning.subtle,
        onStrong: FixedColors.white
      },
      danger: {
        foreground: danger.foreground,
        strong: danger.strong,
        subtle: danger.subtle,
        onStrong: FixedColors.white
      }
    },
    sentiment: {
      positive: success.foreground,
      neutral: ExternalColors.sentimentNeutral,
      negative: danger.foreground
    },
    integrations: {
      whatsapp
    },
    overlay: {
      scrim: 'rgba(0,0,0,0.5)',
      scrimSoft: 'rgba(0,0,0,0.15)',
      toast: 'rgba(0,0,0,0.85)'
    },
    shadow: {
      default: FixedColors.black
    },
    fixed: FixedColors,
    external: ExternalColors,
    illustration: IllustrationColors
  }
}

export const LightColors = createThemeColors('light')
export const DarkColors = createThemeColors('dark')
